<template>
  <div class="login-split-page">
    <ServerError v-if="ServerError" />

    <!-- SnackBar Notification -->
    <v-snackbar v-model="showSnackBar" :color="snackColor" top right :timeout="timeout">
      <span style="color: #FFFFFF; font-weight: 600;">
        {{ msg }}
      </span>
      <template v-slot:action="{ attrs }">
        <v-btn text color="#FFFFFF" v-bind="attrs" @click="showSnackBar = false">
          <v-icon style="color: #FFFFFF">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <div class="split-container">
      <!-- LEFT SIDE: Warm Ivory Showcase & Branding -->
      <div class="split-left-panel">
        <!-- Right Background Image Overlay with seamless fade mask -->
        <div class="left-panel-bg-image"></div>

        <div class="left-content-overlay">
          <div class="left-top-brand">
            <router-link to="/" class="brand-logo-link">
              <div class="logo-mark">
                <svg viewBox="0 0 100 100" class="diamond-svg">
                  <polygon points="50,12 85,38 50,88 15,38" fill="none" stroke="#9E6D2B" stroke-width="5" stroke-linejoin="round" />
                  <line x1="15" y1="38" x2="85" y2="38" stroke="#9E6D2B" stroke-width="4" />
                  <polyline points="32,12 28,38 50,88 72,38 68,12" fill="none" stroke="#9E6D2B" stroke-width="4" stroke-linejoin="round" />
                  <polyline points="32,12 50,38 68,12" fill="none" stroke="#9E6D2B" stroke-width="4" />
                </svg>
              </div>
              <div class="logo-text-block">
                <div class="brand-name">JEWEL<span class="brand-highlight">BIZ</span></div>
                <div class="brand-subtext">SMART JEWELLERY BUSINESS MANAGEMENT</div>
              </div>
            </router-link>

            <!-- Mobile-only back button in top brand bar -->
            <router-link to="/" class="mobile-top-back-btn d-inline-flex d-md-none">
              <v-icon small class="mobile-back-icon">mdi-arrow-left</v-icon>
              <span>Back to Landing</span>
            </router-link>
          </div>

          <div class="left-middle-content">
            <div class="gold-kicker-bar"></div>
            <h1 class="split-left-headline">
              Manage. Grow.<br />
              <span class="headline-serif-gold">Shine with JewelBiz.</span>
            </h1>
            <p class="split-left-desc">
              Streamline sales, inventory, customers, and finances all in one smart platform designed for your success.
            </p>
          </div>

          <div class="left-bottom-kicker">
            <div class="kicker-stacked">
              <span>TRADITION</span>
              <span>MEETS</span>
              <span>TECHNOLOGY</span>
            </div>
            <div class="kicker-line"></div>
          </div>
        </div>
      </div>

      <!-- RIGHT SIDE: Glassmorphic Floating Card Login Form -->
      <div class="split-right-panel">
        <div class="glass-bg-orb orb-1"></div>
        <div class="glass-bg-orb orb-2"></div>

        <!-- Top Navigation Bar with Back Button to Landing Page -->
        <div class="right-panel-topbar">
          <router-link to="/" class="top-back-landing-btn" id="btn-back-to-landing">
            <v-icon small class="back-btn-icon">mdi-arrow-left</v-icon>
            <span>Back to Landing Page</span>
          </router-link>
        </div>

        <div class="login-card-container">
          <!-- Ornament Header (Line - Diamond Icon - Line) -->
          <div class="login-ornament-header">
            <div class="ornament-line"></div>
            <div class="ornament-diamond-box">
              <svg viewBox="0 0 100 100" class="mini-diamond-svg">
                <polygon points="50,12 85,38 50,88 15,38" fill="none" stroke="#9E6D2B" stroke-width="5" stroke-linejoin="round" />
                <line x1="15" y1="38" x2="85" y2="38" stroke="#9E6D2B" stroke-width="4" />
                <polyline points="32,12 28,38 50,88 72,38 68,12" fill="none" stroke="#9E6D2B" stroke-width="4" stroke-linejoin="round" />
                <polyline points="32,12 50,38 68,12" fill="none" stroke="#9E6D2B" stroke-width="4" />
              </svg>
            </div>
            <div class="ornament-line"></div>
          </div>

          <!-- Standard Login Form -->
          <div v-if="!chooseFlag && !pretoken && !forgotMode" class="login-form-wrapper">
            <div class="welcome-kicker">WELCOME BACK</div>
            <h2 class="login-title">Log In</h2>
            <p class="login-subtext">Sign in to continue to your account</p>

            <v-form @submit.prevent="validateInput" class="login-v-form">
              <!-- Email Group -->
              <div class="input-field-wrap mb-4">
                <label class="field-label">Email Address / Username</label>
                <v-text-field
                  v-model="email"
                  prepend-inner-icon="mdi-email-outline"
                  placeholder="Enter your registered email"
                  outlined
                  dense
                  type="email"
                  hide-details
                  class="bright-custom-input"
                ></v-text-field>
              </div>

              <!-- Password Group -->
              <div class="input-field-wrap mb-2">
                <label class="field-label">Password</label>
                <v-text-field
                  v-model="password"
                  prepend-inner-icon="mdi-lock-outline"
                  :append-icon="show1 ? 'mdi-eye' : 'mdi-eye-off'"
                  :type="show1 ? 'text' : 'password'"
                  @click:append="show1 = !show1"
                  placeholder="Enter your password"
                  outlined
                  dense
                  hide-details
                  class="bright-custom-input"
                  @keyup.enter="validateInput"
                ></v-text-field>
              </div>

              <!-- Forgot Password Link Row -->
              <div class="login-helper-row mb-5">
                <a href="#" class="forgot-pw-link" @click.prevent="openForgotPassword">
                  Forgot Password?
                </a>
              </div>

              <!-- Submit Login Button -->
              <button
                type="button"
                class="login-btn-gold"
                @click="validateInput"
              >
                Login <span class="btn-arrow-symbol">→</span>
              </button>
            </v-form>
          </div>

          <!-- Forgot Password Multi-Stage Flow -->
          <div v-else-if="forgotMode" class="login-form-wrapper forgot-flow-wrapper">
            <!-- Stage 1: Request OTP -->
            <div v-if="forgotStage === 1">
              <div class="welcome-kicker">RECOVER ACCESS</div>
              <h2 class="login-title">Forgot Password</h2>
              <p class="login-subtext">Enter your email to receive a 6-digit verification code</p>

              <v-form @submit.prevent="sendForgotOtp" class="login-v-form">
                <div class="input-field-wrap mb-5">
                  <label class="field-label">Registered Email Address</label>
                  <v-text-field
                    v-model="forgotEmail"
                    prepend-inner-icon="mdi-email-outline"
                    placeholder="e.g. admin@jewellery.com"
                    outlined
                    dense
                    type="email"
                    hide-details
                    class="bright-custom-input"
                    @keyup.enter="sendForgotOtp"
                  ></v-text-field>
                </div>

                <button
                  type="button"
                  class="login-btn-gold mb-3"
                  :disabled="forgotLoading"
                  @click="sendForgotOtp"
                >
                  <span v-if="forgotLoading" class="btn-loader mr-2"></span>
                  <span>{{ forgotLoading ? 'Sending Code...' : 'Send Verification Code' }}</span>
                  <span v-if="!forgotLoading" class="btn-arrow-symbol">→</span>
                </button>

                <div class="text-center mt-3">
                  <button type="button" class="back-link-btn" @click="closeForgotPassword">
                    ← Back to Login
                  </button>
                </div>
              </v-form>
            </div>

            <!-- Stage 2: Enter OTP -->
            <div v-else-if="forgotStage === 2">
              <div class="welcome-kicker">SECURITY VERIFICATION</div>
              <h2 class="login-title">Enter Code</h2>
              <p class="login-subtext">
                We sent a 6-digit code to <strong class="gold-text-highlight">{{ maskedEmail || forgotEmail }}</strong>
              </p>

              <v-form @submit.prevent="verifyForgotOtp" class="login-v-form">
                <div class="input-field-wrap mb-3">
                  <label class="field-label">6-Digit Verification Code</label>
                  <v-text-field
                    v-model="otpCode"
                    prepend-inner-icon="mdi-shield-key-outline"
                    placeholder="Enter 6-digit code"
                    outlined
                    dense
                    maxlength="6"
                    hide-details
                    class="bright-custom-input otp-input-field"
                    @keyup.enter="verifyForgotOtp"
                  ></v-text-field>
                </div>

                <div class="otp-resend-row mb-5">
                  <span v-if="resendTimer > 0" class="timer-text">
                    Resend code in <strong class="gold-text-highlight">{{ resendTimer }}s</strong>
                  </span>
                  <button
                    v-else
                    type="button"
                    class="resend-btn"
                    :disabled="forgotLoading"
                    @click="resendOtp"
                  >
                    Resend Code
                  </button>
                  <button type="button" class="change-email-btn" @click="forgotStage = 1">
                    Change Email
                  </button>
                </div>

                <button
                  type="button"
                  class="login-btn-gold mb-3"
                  :disabled="forgotLoading"
                  @click="verifyForgotOtp"
                >
                  <span v-if="forgotLoading" class="btn-loader mr-2"></span>
                  <span>{{ forgotLoading ? 'Verifying...' : 'Verify Code' }}</span>
                  <span v-if="!forgotLoading" class="btn-arrow-symbol">→</span>
                </button>

                <div class="text-center mt-3">
                  <button type="button" class="back-link-btn" @click="closeForgotPassword">
                    ← Cancel & Back to Login
                  </button>
                </div>
              </v-form>
            </div>

            <!-- Stage 3: Set New Password -->
            <div v-else-if="forgotStage === 3">
              <div class="welcome-kicker">NEW CREDENTIALS</div>
              <h2 class="login-title">Reset Password</h2>
              <p class="login-subtext">Create a strong new password for your account</p>

              <v-form @submit.prevent="resetPasswordSubmit" class="login-v-form">
                <div class="input-field-wrap mb-4">
                  <label class="field-label">New Password</label>
                  <v-text-field
                    v-model="newPassword"
                    prepend-inner-icon="mdi-lock-outline"
                    :append-icon="showNewPw ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="showNewPw ? 'text' : 'password'"
                    @click:append="showNewPw = !showNewPw"
                    placeholder="At least 6 characters"
                    outlined
                    dense
                    hide-details
                    class="bright-custom-input"
                  ></v-text-field>
                </div>

                <div class="input-field-wrap mb-5">
                  <label class="field-label">Confirm New Password</label>
                  <v-text-field
                    v-model="confirmPassword"
                    prepend-inner-icon="mdi-lock-check-outline"
                    :append-icon="showConfirmPw ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="showConfirmPw ? 'text' : 'password'"
                    @click:append="showConfirmPw = !showConfirmPw"
                    placeholder="Re-enter new password"
                    outlined
                    dense
                    hide-details
                    class="bright-custom-input"
                    @keyup.enter="resetPasswordSubmit"
                  ></v-text-field>
                </div>

                <button
                  type="button"
                  class="login-btn-gold mb-3"
                  :disabled="forgotLoading"
                  @click="resetPasswordSubmit"
                >
                  <span v-if="forgotLoading" class="btn-loader mr-2"></span>
                  <span>{{ forgotLoading ? 'Updating Password...' : 'Reset Password & Sign In' }}</span>
                  <span v-if="!forgotLoading" class="btn-arrow-symbol">→</span>
                </button>

                <div class="text-center mt-3">
                  <button type="button" class="back-link-btn" @click="closeForgotPassword">
                    ← Cancel & Back to Login
                  </button>
                </div>
              </v-form>
            </div>

            <!-- Stage 4: Success Screen -->
            <div v-else-if="forgotStage === 4" class="text-center py-2">
              <div class="success-icon-badge mb-3">
                <v-icon large color="#2E7D32">mdi-check-decagram</v-icon>
              </div>
              <div class="welcome-kicker" style="color: #2E7D32;">ALL SET</div>
              <h2 class="login-title">Password Reset!</h2>
              <p class="login-subtext">
                Your password has been updated successfully. You can now log in to your JewelBiz account.
              </p>

              <button
                type="button"
                class="login-btn-gold mt-4"
                @click="finishAndGoToLogin"
              >
                Continue to Login <span class="btn-arrow-symbol">→</span>
              </button>
            </div>
          </div>

          <!-- Choose Position / Multi-Role Segment -->
          <div v-else class="login-form-wrapper">
            <div class="welcome-kicker">SELECT YOUR POSITION</div>
            <h2 class="login-title">Choose Role</h2>
            <p class="login-subtext">Select your role to access the workspace</p>

            <div class="role-selector-box mb-5">
              <v-radio-group v-model="position" class="bright-radio-group" hide-details>
                <v-radio
                  v-for="(item, i) in positionList"
                  :key="i"
                  :label="item.name"
                  :value="item._id"
                  color="#9E6D2B"
                  class="bright-radio py-1"
                ></v-radio>
              </v-radio-group>
            </div>

            <button
              type="button"
              class="login-btn-gold"
              @click="applyRole()"
            >
              Continue to Workspace <span class="btn-arrow-symbol">→</span>
            </button>
          </div>
        </div>

        <!-- Counterbalance spacer for vertical symmetry -->
        <div class="right-panel-bottom-spacer"></div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import store from "./../../store";

export default {
  name: "LoginPage",
  data() {
    return {
      ServerError: false,
      showSnackBar: false,
      snackColor: "#9E6D2B",
      timeout: 5000,
      msg: null,
      email: "",
      password: "",
      chooseFlag: false,
      position: null,
      positionList: null,
      pretoken: null,
      show1: false,

      // Forgot Password Multi-Stage States
      forgotMode: false,
      forgotStage: 1, // 1: Email, 2: OTP, 3: New Password, 4: Success
      forgotLoading: false,
      forgotEmail: "",
      maskedEmail: "",
      otpCode: "",
      resetToken: "",
      newPassword: "",
      confirmPassword: "",
      showNewPw: false,
      showConfirmPw: false,
      resendTimer: 0,
      timerInterval: null
    };
  },

  mounted() {
    if (
      this.$route.path === "/forgot-password" ||
      this.$route.name === "forgotPassword" ||
      (this.$route.query && (this.$route.query.forgot === "true" || this.$route.query.forgot === "1"))
    ) {
      this.openForgotPassword();
    }
  },

  beforeDestroy() {
    this.clearIntervalTimer();
  },

  methods: {
    validateInput() {
      if (!this.email || !this.email.trim()) {
        this.msg = "Please provide your email address";
        this.snackColor = "#C62828";
        this.showSnackBar = true;
        return;
      } else if (!this.password) {
        this.msg = "Please provide your password";
        this.snackColor = "#C62828";
        this.showSnackBar = true;
        return;
      } else {
        this.login();
      }
    },
    login() {
      var userData = {};
      userData["username"] = this.email.trim();
      userData["email"] = this.email.trim();
      userData["password"] = this.password;
      store.commit("appLoading", true);
      axios({
        method: "POST",
        url: "/user/login",
        data: userData
      })
        .then((response) => {
          store.commit("appLoading", false);
          if (response.data.status) {
            store.commit("userType", response.data.mainRole);
            localStorage.setItem("role", response.data.mainRole);
            store.commit("userData", response.data.data);
            store.commit("fullData", response.data);
            store.commit("id", response.data.data.id);
            store.commit("mustChangePassword", response.data.data.mustChangePassword);

            store.commit("loginUser", response.data.data.token);
            localStorage.setItem("id", response.data.data.id);
            this.userinfo();
          } else {
            this.msg = response.data.msg;
            this.snackColor = "#C62828";
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          store.commit("appLoading", false);
          this.ServerError = true;
          console.log(err);
        });
    },
    userinfo() {
      axios({
        method: "GET",
        url: "/user/info",
        headers: {
          "x-auth-token": localStorage.getItem("token")
        },
        params: {
          id: localStorage.getItem("id")
        }
      })
        .then((response) => {
          if (response.data.status) {
            this.$store.commit("menu", response.data.menus);
            localStorage.setItem("GSTstatus", response.data.data.taxType);
          } else {
            store.commit("sessionOut", true);
            return;
          }
        })
        .catch((err) => {
          console.log(err);
        });
    },

    // ==========================================
    // FORGOT PASSWORD OTP METHODS
    // ==========================================
    openForgotPassword() {
      this.forgotMode = true;
      this.forgotStage = 1;
      this.forgotEmail = this.email ? this.email.trim() : "";
      this.otpCode = "";
      this.resetToken = "";
      this.newPassword = "";
      this.confirmPassword = "";
    },
    closeForgotPassword() {
      this.forgotMode = false;
      this.forgotStage = 1;
      this.clearIntervalTimer();
    },
    clearIntervalTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    },
    startResendTimer(seconds = 60) {
      this.clearIntervalTimer();
      this.resendTimer = seconds;
      this.timerInterval = setInterval(() => {
        if (this.resendTimer > 0) {
          this.resendTimer--;
        } else {
          this.clearIntervalTimer();
        }
      }, 1000);
    },
    sendForgotOtp() {
      if (!this.forgotEmail || !this.forgotEmail.trim()) {
        this.msg = "Please enter your registered email address";
        this.snackColor = "#C62828";
        this.showSnackBar = true;
        return;
      }

      this.forgotLoading = true;
      axios({
        method: "POST",
        url: "/user/forgot-password/send-otp",
        data: { email: this.forgotEmail.trim() }
      })
        .then((response) => {
          this.forgotLoading = false;
          if (response.data && response.data.status) {
            this.maskedEmail = response.data.maskedEmail || this.forgotEmail.trim();
            this.forgotStage = 2;
            this.otpCode = "";
            this.startResendTimer(60);
            this.msg = response.data.msg || "Verification code sent to your email";
            this.snackColor = "#2E7D32";
            this.showSnackBar = true;
          } else {
            this.msg = (response.data && response.data.msg) || "Could not find account with that email.";
            this.snackColor = "#C62828";
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.forgotLoading = false;
          this.msg = "Failed to send verification code. Please try again.";
          this.snackColor = "#C62828";
          this.showSnackBar = true;
          console.error(err);
        });
    },
    resendOtp() {
      if (this.resendTimer > 0) return;
      this.sendForgotOtp();
    },
    verifyForgotOtp() {
      if (!this.otpCode || this.otpCode.trim().length !== 6) {
        this.msg = "Please enter the complete 6-digit verification code";
        this.snackColor = "#C62828";
        this.showSnackBar = true;
        return;
      }

      this.forgotLoading = true;
      axios({
        method: "POST",
        url: "/user/forgot-password/verify-otp",
        data: {
          email: this.forgotEmail.trim(),
          otp: this.otpCode.trim()
        }
      })
        .then((response) => {
          this.forgotLoading = false;
          if (response.data && response.data.status) {
            this.resetToken = response.data.resetToken;
            this.forgotStage = 3;
            this.clearIntervalTimer();
            this.msg = "Code verified! Please create your new password.";
            this.snackColor = "#2E7D32";
            this.showSnackBar = true;
          } else {
            this.msg = (response.data && response.data.msg) || "Invalid or expired verification code.";
            this.snackColor = "#C62828";
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.forgotLoading = false;
          this.msg = "Verification failed. Please try again.";
          this.snackColor = "#C62828";
          this.showSnackBar = true;
          console.error(err);
        });
    },
    resetPasswordSubmit() {
      if (!this.newPassword || this.newPassword.length < 6) {
        this.msg = "Password must be at least 6 characters long";
        this.snackColor = "#C62828";
        this.showSnackBar = true;
        return;
      }
      if (this.newPassword !== this.confirmPassword) {
        this.msg = "Passwords do not match. Please re-enter.";
        this.snackColor = "#C62828";
        this.showSnackBar = true;
        return;
      }

      this.forgotLoading = true;
      axios({
        method: "POST",
        url: "/user/forgot-password/reset-password",
        data: {
          email: this.forgotEmail.trim(),
          resetToken: this.resetToken,
          newPassword: this.newPassword
        }
      })
        .then((response) => {
          this.forgotLoading = false;
          if (response.data && response.data.status) {
            this.forgotStage = 4;
            this.email = this.forgotEmail.trim();
            this.password = "";
            this.msg = response.data.msg || "Password reset successfully!";
            this.snackColor = "#2E7D32";
            this.showSnackBar = true;
          } else {
            this.msg = (response.data && response.data.msg) || "Failed to reset password.";
            this.snackColor = "#C62828";
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.forgotLoading = false;
          this.msg = "Password reset failed. Please try again.";
          this.snackColor = "#C62828";
          this.showSnackBar = true;
          console.error(err);
        });
    },
    finishAndGoToLogin() {
      this.forgotMode = false;
      this.forgotStage = 1;
      this.email = this.forgotEmail.trim();
      this.password = "";
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@500;600;700;800&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,500;1,600&family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400;1,500&display=swap');

.login-split-page {
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif !important;
  background-color: #FAF8F5;
  color: #1E1815;
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
}

.split-container {
  display: flex;
  min-height: 100vh;
  width: 100%;
}

/* LEFT PANEL - WARM IVORY SHOWCASE */
.split-left-panel {
  width: 50%;
  min-height: 100vh;
  background-color: #FAF8F5;
  position: relative;
  overflow: hidden;
  border-right: 1px solid #EFE8DD;
  display: flex;
  flex-direction: column;
}

.left-panel-bg-image {
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  background-image: url('/landing/banner_wedding_bangles.jpg');
  background-size: cover;
  background-position: center right;
  mask-image: linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 30%, rgba(0,0,0,0.95) 75%, rgba(0,0,0,1) 100%);
  -webkit-mask-image: linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 30%, rgba(0,0,0,0.95) 75%, rgba(0,0,0,1) 100%);
  opacity: 0.95;
  z-index: 1;
  transition: all 0.3s ease;
}

.left-content-overlay {
  position: relative;
  z-index: 2;
  height: 100%;
  min-height: 100vh;
  padding: clamp(24px, 4vh, 48px) clamp(24px, 5vw, 64px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box;
}

.left-top-brand {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.mobile-top-back-btn {
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(158, 109, 43, 0.35);
  border-radius: 20px;
  color: #7D551E;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow: 0 2px 8px rgba(158, 109, 43, 0.1);
  transition: all 0.2s ease;
}

.mobile-top-back-btn:hover {
  background: #9E6D2B;
  color: #FFFFFF !important;
}

.mobile-top-back-btn:hover .mobile-back-icon {
  color: #FFFFFF !important;
}

.mobile-back-icon {
  color: #9E6D2B !important;
  font-size: 16px !important;
}

.brand-logo-link {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
}

.logo-mark {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.diamond-svg {
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 2px 4px rgba(158, 109, 43, 0.18));
}

.logo-text-block {
  display: flex;
  flex-direction: column;
}

.brand-name {
  font-family: 'Cinzel', 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(18px, 2vw, 22px);
  font-weight: 700;
  letter-spacing: 2.2px;
  color: #1E1815;
  line-height: 1.1;
}

.brand-highlight {
  color: #9E6D2B;
}

.brand-subtext {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 8.5px;
  letter-spacing: 2px;
  font-weight: 600;
  color: #8C7D6F;
  text-transform: uppercase;
  margin-top: 3px;
}

.left-middle-content {
  margin: clamp(16px, 3vh, 32px) 0;
  max-width: 460px;
}

.gold-kicker-bar {
  width: 40px;
  height: 1.5px;
  background-color: #9E6D2B;
  margin-bottom: clamp(12px, 2vh, 20px);
}

.split-left-headline {
  font-family: 'Cormorant Garamond', Garamond, Georgia, serif;
  font-size: clamp(32px, 3.5vw, 48px);
  line-height: 1.12;
  color: #1E1815;
  font-weight: 600;
  letter-spacing: -0.3px;
  margin-bottom: clamp(10px, 1.8vh, 16px);
}

.headline-serif-gold {
  font-family: 'Cormorant Garamond', Garamond, Georgia, serif;
  color: #A2722C;
  font-weight: 500;
}

.split-left-desc {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: clamp(13.5px, 1.1vw, 15px);
  line-height: 1.65;
  color: #5C5248;
  max-width: 440px;
  margin-bottom: 0;
}

.left-bottom-kicker {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.kicker-stacked {
  display: flex;
  flex-direction: column;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 2.5px;
  color: #9E6D2B;
  line-height: 1.45;
}

.kicker-line {
  width: 40px;
  height: 1.5px;
  background-color: #9E6D2B;
  margin-top: 4px;
}

/* RIGHT PANEL - GLASSMORPHIC CONTAINER & AMBIENT ORBS */
.split-right-panel {
  width: 50%;
  min-height: 100vh;
  background-color: #FAF7F2;
  background-image: 
    radial-gradient(circle at 15% 15%, rgba(245, 235, 220, 0.9) 0%, transparent 45%),
    radial-gradient(circle at 85% 85%, rgba(238, 222, 200, 0.7) 0%, transparent 55%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: clamp(20px, 3vh, 32px) clamp(20px, 3.5vw, 44px);
  position: relative;
  overflow-y: auto;
  box-sizing: border-box;
}

/* TOP NAVIGATION - BACK TO LANDING */
.right-panel-topbar {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  z-index: 10;
  flex-shrink: 0;
  margin-bottom: clamp(12px, 2vh, 20px);
}

.top-back-landing-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 18px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(158, 109, 43, 0.35);
  border-radius: 30px;
  color: #7D551E;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.2px;
  text-decoration: none;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 4px 14px rgba(158, 109, 43, 0.08);
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.top-back-landing-btn:hover {
  background: #9E6D2B;
  border-color: #9E6D2B;
  color: #FFFFFF !important;
  box-shadow: 0 6px 20px rgba(158, 109, 43, 0.26);
  transform: translateY(-2px);
}

.top-back-landing-btn:hover .back-btn-icon {
  color: #FFFFFF !important;
  transform: translateX(-3px);
}

.back-btn-icon {
  color: #9E6D2B !important;
  font-size: 18px !important;
  transition: transform 0.2s ease, color 0.2s ease;
}

.glass-bg-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(50px);
  pointer-events: none;
  z-index: 1;
}

.orb-1 {
  width: 340px;
  height: 340px;
  top: 8%;
  right: 8%;
  background: radial-gradient(circle, rgba(212, 175, 55, 0.22) 0%, rgba(197, 148, 62, 0.1) 60%, transparent 100%);
}

.orb-2 {
  width: 300px;
  height: 300px;
  bottom: 10%;
  left: 8%;
  background: radial-gradient(circle, rgba(158, 109, 43, 0.18) 0%, rgba(181, 130, 56, 0.08) 60%, transparent 100%);
}

.login-card-container {
  width: 100%;
  max-width: 430px;
  position: relative;
  z-index: 2;
  margin: auto 0;
  border-radius: 24px;
  padding: clamp(28px, 4vh, 42px) clamp(24px, 3.5vw, 40px);
  background: rgba(255, 255, 255, 0.74) !important;
  backdrop-filter: blur(24px) saturate(180%) !important;
  -webkit-backdrop-filter: blur(24px) saturate(180%) !important;
  border: 1px solid rgba(255, 255, 255, 0.9) !important;
  box-shadow: 
    0 25px 50px -12px rgba(30, 24, 21, 0.1),
    0 8px 24px rgba(158, 109, 43, 0.06),
    inset 0 0 0 1px rgba(255, 255, 255, 0.8),
    inset 0 1.5px 2px rgba(255, 255, 255, 1);
  transition: all 0.35s ease;
}

.right-panel-bottom-spacer {
  width: 100%;
  height: 38px;
  flex-shrink: 0;
  pointer-events: none;
}

.login-card-container:hover {
  box-shadow: 
    0 32px 64px -12px rgba(30, 24, 21, 0.13),
    0 12px 28px rgba(158, 109, 43, 0.08),
    inset 0 0 0 1px rgba(255, 255, 255, 0.9),
    inset 0 1.5px 2px rgba(255, 255, 255, 1);
  transform: translateY(-2px);
}

.login-ornament-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: clamp(14px, 2vh, 20px);
}

.ornament-line {
  flex: 1;
  max-width: 70px;
  height: 1.5px;
  background-color: #D1B896;
}

.ornament-diamond-box {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mini-diamond-svg {
  width: 100%;
  height: 100%;
}

.login-form-wrapper {
  text-align: center;
}

.welcome-kicker {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 2.5px;
  color: #9E6D2B;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.login-title {
  font-family: 'Cormorant Garamond', Garamond, Georgia, serif;
  font-size: clamp(32px, 3vw, 40px);
  font-weight: 600;
  color: #1E1815;
  margin-bottom: 4px;
  line-height: 1.1;
}

.login-subtext {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 13px;
  color: #6E6358;
  margin-bottom: clamp(18px, 2.5vh, 28px);
}

.login-v-form {
  text-align: left;
}

.input-field-wrap {
  display: flex;
  flex-direction: column;
}

.field-label {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  color: #383028;
  margin-bottom: 5px;
}

.bright-custom-input >>> .v-input__slot {
  background-color: rgba(255, 255, 255, 0.75) !important;
  backdrop-filter: blur(10px) !important;
  -webkit-backdrop-filter: blur(10px) !important;
  border: 1px solid rgba(226, 215, 199, 0.85) !important;
  border-radius: 12px !important;
  min-height: 46px !important;
  box-shadow: 0 2px 6px rgba(30, 24, 21, 0.02) !important;
  padding: 0 14px !important;
  transition: all 0.25s ease !important;
}

.bright-custom-input.v-input--is-focused >>> .v-input__slot {
  border-color: #9E6D2B !important;
  background-color: rgba(255, 255, 255, 0.95) !important;
  box-shadow: 0 0 0 3px rgba(158, 109, 43, 0.14), 0 4px 12px rgba(158, 109, 43, 0.05) !important;
}

.bright-custom-input >>> .v-icon {
  color: #9E6D2B !important;
  font-size: 20px !important;
}

.bright-custom-input >>> input {
  font-family: 'Plus Jakarta Sans', sans-serif !important;
  color: #1E1815 !important;
  font-size: 14px !important;
}

/* Chrome / Browser Autofill Overrides */
.bright-custom-input >>> input:-webkit-autofill,
.bright-custom-input >>> input:-webkit-autofill:hover,
.bright-custom-input >>> input:-webkit-autofill:focus,
.bright-custom-input >>> input:-webkit-autofill:active {
  -webkit-text-fill-color: #1E1815 !important;
  -webkit-box-shadow: 0 0 0 1000px rgba(253, 250, 245, 0.95) inset !important;
  box-shadow: 0 0 0 1000px rgba(253, 250, 245, 0.95) inset !important;
  transition: background-color 5000s ease-in-out 0s !important;
}

.login-btn-gold {
  width: 100%;
  height: 48px;
  background: linear-gradient(135deg, #B58238 0%, #905F1E 100%);
  color: #FFFFFF;
  border: none;
  border-radius: 12px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 6px 18px rgba(158, 109, 43, 0.24);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.login-btn-gold:hover {
  background: linear-gradient(135deg, #C28F43 0%, #9B6925 100%);
  box-shadow: 0 8px 22px rgba(158, 109, 43, 0.34);
  transform: translateY(-1px);
}

.btn-arrow-symbol {
  font-size: 18px;
  transition: transform 0.2s ease;
}

.login-btn-gold:hover .btn-arrow-symbol {
  transform: translateX(4px);
}

.bright-radio >>> label {
  color: #1E1815 !important;
  font-family: 'Plus Jakarta Sans', sans-serif !important;
  font-size: 14px !important;
  font-weight: 500 !important;
}

/* FORGOT PASSWORD FLOW STYLES */
.login-helper-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 4px;
}

.forgot-pw-link {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  color: #9E6D2B;
  text-decoration: none;
  transition: all 0.2s ease;
}

.forgot-pw-link:hover {
  color: #7D551E;
  text-decoration: underline;
}

.gold-text-highlight {
  color: #9E6D2B;
  font-weight: 700;
  word-break: break-all;
}

.otp-input-field >>> input {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace !important;
  font-size: 20px !important;
  font-weight: 700 !important;
  letter-spacing: 6px !important;
  text-align: center !important;
  color: #3F053C !important;
}

.otp-resend-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12.5px;
  color: #6E6358;
  padding: 0 4px;
}

.timer-text {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12.5px;
  color: #6E6358;
}

.resend-btn,
.change-email-btn,
.back-link-btn {
  background: none;
  border: none;
  padding: 0;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  color: #9E6D2B;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s ease;
}

.resend-btn:hover,
.change-email-btn:hover,
.back-link-btn:hover {
  color: #7D551E;
  text-decoration: underline;
}

.back-link-btn {
  font-size: 13px;
  color: #7D551E;
}

.success-icon-badge {
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background: #E8F5E9;
  border: 2px solid #A5D6A7;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
  box-shadow: 0 4px 14px rgba(46, 125, 50, 0.12);
}

.btn-loader {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-radius: 50%;
  border-top-color: #FFFFFF;
  animation: spin 0.8s ease-in-out infinite;
  -webkit-animation: spin 0.8s ease-in-out infinite;
}

@keyframes spin {
  to { -webkit-transform: rotate(360deg); transform: rotate(360deg); }
}

.login-btn-gold:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none !important;
}

/* RESPONSIVE BREAKPOINTS */
@media (max-width: 1200px) {
  .left-panel-bg-image {
    background-position: center right 20%;
  }
}

@media (max-width: 960px) {
  .login-split-page {
    height: auto;
    min-height: 100vh;
    overflow-y: auto;
  }
  .split-container {
    flex-direction: column;
    min-height: 100vh;
  }
  /* Hide the image & showcase panel completely on mobile */
  .split-left-panel {
    display: none !important;
  }
  .split-right-panel {
    width: 100%;
    min-height: 100vh;
    padding: clamp(16px, 3vh, 28px) clamp(16px, 4vw, 24px) 32px;
    justify-content: space-between;
  }
  .right-panel-topbar {
    width: 100%;
    justify-content: flex-end;
    margin-bottom: 16px;
  }
  .login-card-container {
    width: 100%;
    max-width: 440px;
    padding: clamp(24px, 3.5vh, 32px) clamp(18px, 4vw, 28px);
    margin: auto 0;
  }
  .login-title {
    font-size: clamp(26px, 6vw, 32px);
  }
  .login-subtext {
    font-size: 13px;
    margin-bottom: 20px;
  }
  .right-panel-bottom-spacer {
    display: block;
    height: 12px;
  }
}

@media (max-height: 700px) and (min-width: 961px) {
  .left-content-overlay {
    padding: 24px 6%;
  }
  .login-card-container {
    padding: 28px 32px;
  }
  .split-left-headline {
    font-size: 34px;
    margin-bottom: 8px;
  }
  .login-title {
    font-size: 32px;
  }
  .login-subtext {
    margin-bottom: 16px;
  }
}
</style>